import { Component, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { lookupUserEmails } from '../../utils/user-email.util';
import { resolveActiveOrganizationId } from '../../utils/workspace-scope.util';
import { ChartjsComponent, SplitPaneComponent } from '@components';
import {
  DevopsFacade,
  DocsFacade,
  WorkspaceFacade,
  WorkflowSectionKey,
  WorkflowSectionStatus,
  WorkflowFacade,
} from '@state';
import {
  findHomeDashboardCard,
  HOME_DASHBOARD_CARDS,
  HomeDashboardCard,
  HomeDashboardSectionKey,
  NotificationsService,
  OrganizationUsersService,
  ProjectUsersService,
  QuotationsService,
} from '@services';

@Component({
  selector: 'app-home',
  templateUrl: './home.page.html',
  styleUrls: ['./home.page.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, SplitPaneComponent, ChartjsComponent],
})
export class HomePage implements OnInit, OnDestroy {
  isExpanded = false;
  shortcutFabExpanded = false;
  selectedSectionTitle: string | null = null;
  selectedSectionKey: HomeDashboardSectionKey | null = null;
  currentProjectName: string = '—';
  currentOrganizationName: string = '—';
  collapseProjects: boolean = false;
  collapseOrganizations: boolean = false;
  readonly projectHomeCards = HOME_DASHBOARD_CARDS.filter((card) => card.group === 'project');
  readonly organizationHomeCards = HOME_DASHBOARD_CARDS.filter((card) => card.group === 'organization');
  overview = { lastUpdated: 'Recently' };
  readonly contactTopics = [
    'General support',
    'Sales',
    'Billing',
    'Technical issue',
    'Feature request',
    'Training',
    'Documentation',
  ];
  readonly contactUpcomingFeatures = [
    'Quotation email templates',
    'Kanban swimlanes',
    'Advanced user roles',
  ];

  agentCounts: Record<string, { last7d: number; last30d?: number; delta?: number }> = {
    overview: { last7d: 3 },
    tickets: { last7d: 12, delta: 8 },
    repos: { last7d: 2 },
    devops: { last7d: 5 },
    workspace: { last7d: 4 },
    users: { last7d: 3 },
    notifications: { last7d: 7 },
    define: { last7d: 14 },
    quotations: { last7d: 5 },
    contact: { last7d: 2 },
  };

  private store = inject(WorkspaceFacade);
  private projectUsersService = inject(ProjectUsersService);
  private orgUsersService = inject(OrganizationUsersService);
  public workflow = inject(WorkflowFacade);
  private docs = inject(DocsFacade);
  public notificationsService = inject(NotificationsService);
  public devopsStore = inject(DevopsFacade);
  private quotationsService = inject(QuotationsService);
  private router = inject(Router);

  private activeProjectId = computed(() => this.store.activeProjectId());
  private activeOrganizationId = computed(() =>
    resolveActiveOrganizationId({
      selectedOrganizationId: this.store.selectedOrganizationId(),
      activeProjectId: this.activeProjectId(),
      orgProjects: this.store.orgProjects(),
      organizations: this.store.organizations(),
    })
  );
  quotationsLoading = false;
  quotationsError = '';
  quotationsSummary = {
    total: 0,
    draft: 0,
    readyToSend: 0,
    sent: 0,
    history: 0,
    lastUpdatedLabel: '—',
    recentNames: [] as string[],
  };
  private quotationsRequestVersion = 0;
  private membershipRequestVersion = 0;
  private _memo: Record<string, { key: string; value: any }> = {};
  private nowMs = signal(Date.now());
  private userEmailById = signal<Record<string, string>>({});
  private relativeTimeTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    effect(() => {
      const selectedProjectId = this.store.selectedProjectId();
      const selectedOrgId = this.store.selectedOrganizationId();
      const projects = this.store.projects();
      const orgs = this.store.organizations();
      const orgProjects = this.store.orgProjects();

      let proj = projects.find((p: any) => p.id === selectedProjectId) ?? null;
      if (!proj) proj = projects[0] ?? null;
      if (proj) {
        void this.workflow.warmProject((proj as any).id);
        this.currentProjectName = (proj as any).name ?? 'Current Project';
        const orgId =
          selectedOrgId ?? (orgProjects.find((op: any) => op.projectId === (proj as any).id)?.organizationId) ?? null;
        if (orgId) {
          const org = orgs.find((o: any) => o.id === orgId) ?? null;
          this.currentOrganizationName = org ? ((org as any).name ?? 'Current Organization') : '—';
        } else {
          this.currentOrganizationName = '—';
        }
      } else {
        this.currentProjectName = '—';
        const org = orgs.find((o: any) => o.id === selectedOrgId) ?? orgs[0] ?? null;
        this.currentOrganizationName = org ? ((org as any).name ?? 'Current Organization') : '—';
      }
    });

    effect(() => {
      const projectId = this.activeProjectId();
      void this.loadQuotationsSummary(projectId);
    });

    effect(() => {
      if (!this.store.initialized()) return;
      const projectId = this.activeProjectId();
      const organizationId = this.activeOrganizationId();
      void this.loadMembershipData(projectId, organizationId);
    });
  }

  ngOnInit(): void {
    this.relativeTimeTimer = setInterval(() => {
      this.nowMs.set(Date.now());
    }, 1000);
    void this.devopsStore.loadOverview();
  }

  ngOnDestroy(): void {
    if (this.relativeTimeTimer) {
      clearInterval(this.relativeTimeTimer);
      this.relativeTimeTimer = null;
    }
  }

  async resyncFromRepo() {
    await this.router.navigate(['/define'], { queryParams: { resync: 1 }, queryParamsHandling: 'merge' });
  }

  toggleShortcutFab(): void {
    this.shortcutFabExpanded = !this.shortcutFabExpanded;
  }

  closeShortcutFab(): void {
    this.shortcutFabExpanded = false;
  }

  async goToCreateProject(): Promise<void> {
    this.closeShortcutFab();
    await this.router.navigate(['/create-project']);
  }

  async goToCreateOrganization(): Promise<void> {
    this.closeShortcutFab();
    await this.router.navigate(['/create-organization']);
  }

  get selectedWorkflowStatus(): WorkflowSectionStatus | null {
    const statuses = this.workflow?.statuses?.() ?? [];
    if (this.selectedSectionKey && this.isWorkflowKey(this.selectedSectionKey)) {
      return statuses.find((status: any) => status.key === this.selectedSectionKey) ?? null;
    }
    const title = (this.selectedSectionTitle ?? '').trim().toLowerCase();
    if (!title) return null;
    return statuses.find((status: any) => (status.title || '').toLowerCase() === title) ?? null;
  }

  get reposSummary(): {
    githubConnected: boolean;
    repoCount: number;
    reposLabel: string;
    openPRs: number;
    lastRefreshedLabel: string;
  } {
    const pid = this.activeProjectId();
    if (!pid) {
      return {
        githubConnected: false,
        repoCount: 0,
        reposLabel: '—',
        openPRs: 0,
        lastRefreshedLabel: '—',
      };
    }

    const all = this.docs.all?.() ?? [];
    const bestForKey = (key: string) => {
      const matches = all.filter((doc: any) => doc?.projectId === pid && doc?.key === key);
      if (!matches.length) return null;
      return matches.reduce((best: any, current: any) => {
        const bestVersion = best?.version ?? 0;
        const currentVersion = current?.version ?? 0;
        if (currentVersion !== bestVersion) return currentVersion > bestVersion ? current : best;
        const bestUpdatedAt =
          Date.parse(String(best?.updatedAt ?? best?.updated ?? best?.createdAt ?? best?.created ?? '')) || 0;
        const currentUpdatedAt =
          Date.parse(String(current?.updatedAt ?? current?.updated ?? current?.createdAt ?? current?.created ?? '')) || 0;
        return currentUpdatedAt > bestUpdatedAt ? current : best;
      }, matches[0]);
    };

    const settingsDoc = bestForKey('repos.settings');
    const stateDoc = bestForKey('repos.state');
    const settings = (settingsDoc?.data ?? null) as any;
    const installationId = settings?.github?.installationId ?? null;
    const githubConnected =
      typeof installationId === 'number' && Number.isFinite(installationId) && installationId > 0;
    const repos = Array.isArray(settings?.github?.repos) ? settings.github.repos : [];
    const repoCount = repos.length;
    const reposLabel =
      repoCount > 0 ? repos.map((repo: any) => `${String(repo?.owner ?? '').trim()}/${String(repo?.name ?? '').trim()}`).filter(Boolean).join(', ') : '—';

    const state = (stateDoc?.data ?? null) as any;
    const repoStateMap = state?.repos && typeof state.repos === 'object' && !Array.isArray(state.repos) ? state.repos : {};
    const openPRs = Object.values(repoStateMap).reduce((sum: number, entry: any) => {
      const pulls = Array.isArray(entry?.pulls) ? entry.pulls : [];
      return sum + pulls.length;
    }, 0);
    const aggregatedLastRefreshedAt = Object.values(repoStateMap).reduce((latest: number | null, entry: any) => {
      const value = typeof entry?.lastRefreshedAt === 'number' ? entry.lastRefreshedAt : null;
      if (value == null) return latest;
      return latest == null ? value : Math.max(latest, value);
    }, null);
    const lastRefreshedAt = aggregatedLastRefreshedAt ?? (typeof state?.lastRefreshedAt === 'number' ? state.lastRefreshedAt : null);
    const lastRefreshedLabel = this.formatRelativeTime(lastRefreshedAt);

    return { githubConnected, repoCount, reposLabel, openPRs, lastRefreshedLabel };
  }

  get defineStatus(): WorkflowSectionStatus | null {
    return (this.workflow?.statuses?.() ?? []).find((status) => status.key === 'define') ?? null;
  }

  get dbDesignStatus(): WorkflowSectionStatus | null {
    return (this.workflow?.statuses?.() ?? []).find((status) => status.key === 'db-design') ?? null;
  }

  get ticketsStatus(): WorkflowSectionStatus | null {
    return (this.workflow?.statuses?.() ?? []).find((status) => status.key === 'tickets') ?? null;
  }

  get defineSummary() {
    return this.workflow.defineSummary();
  }

  get dbDesignSummary() {
    return this.workflow.dbDesignSummary();
  }

  get ticketsSummary() {
    return this.workflow.ticketsSummary();
  }

  get usersSummary() {
    const projectId = this.activeProjectId();
    const organizationId = this.activeOrganizationId();
    const projectUsers = projectId ? this.store.projectUsers().filter((pu) => pu.projectId === projectId) : [];
    const organizationUsers = organizationId
      ? this.store.orgUsers().filter((ou) => ou.organizationId === organizationId)
      : [];
    const recent = [
      ...projectUsers.map((item) => this.toRecentMember(item, 'Project')),
      ...organizationUsers.map((item) => this.toRecentMember(item, 'Organization')),
    ]
      .sort((left, right) => right.sortMs - left.sortMs || left.label.localeCompare(right.label))
      .filter((item, index, all) => all.findIndex((candidate) => candidate.key === item.key) === index)
      .slice(0, 3)
      .map(({ label, meta }) => ({ label, meta }));
    return {
      projectMembers: projectUsers.length,
      organizationMembers: organizationUsers.length,
      pendingInvites: this.notificationsService.invites().length,
      recent,
    };
  }

  get notificationsSummary() {
    const items = this.notificationsService.notifications();
    const active = items.filter((item) => !item.archived);
    const unread = active.filter((item) => !item.read);
    const mentions = active.filter((item) => item.category === 'mention');
    const system = active.filter((item) => item.category === 'system');
    const invites = this.notificationsService.invites();
    const recent = active.slice(0, 3).map((item) => ({
      title: item.title || item.message || 'Notification',
      when: this.formatRelativeTime(this.toTimestamp(item.timestamp)),
    }));
    return {
      total: active.length,
      unread: unread.length,
      mentions: mentions.length,
      system: system.length,
      invites: invites.length,
      recent,
      inviteRows: invites.slice(0, 3),
    };
  }

  get devopsSummary() {
    const platform = this.devopsStore.platform();
    const proxy = this.devopsStore.proxy();
    const selectedTeam = this.devopsStore.selectedTeam();
    const spaces = this.devopsStore.spaces() ?? [];
    const spaceGroups = this.devopsStore.spaceGroups() ?? [];
    const totalCapsules = spaces.reduce((sum, space) => sum + Number(space?.capsuleCount ?? 0), 0);
    const environments = spaceGroups.map((group) => ({
      label: group.label,
      environment: group.environment,
      spaceCount: group.spaces.length,
      capsuleCount: group.spaces.reduce((sum, space) => sum + Number(space?.capsuleCount ?? 0), 0),
    }));
    const spaceRows = spaces.map((space) => ({
      name: space?.name ?? space?.slug ?? '—',
      slug: space?.slug ?? '',
      environment: space?.environment ?? null,
      region: String(space?.region ?? '').trim(),
      capsuleCount: Number(space?.capsuleCount ?? 0),
      matchedRepoCount: Number(space?.matchedRepos?.length ?? 0),
    }));
    const matchSourceLabel =
      selectedTeam?.matchSource === 'project-name'
        ? 'Matched by project name'
        : selectedTeam?.matchSource === 'repo-fallback'
          ? 'Matched by repo fallback'
          : '—';
    return {
      authenticated: !!platform?.authenticated,
      email: String(platform?.email ?? '').trim() || 'No active Code Capsules session',
      proxyStatus: String(proxy?.status ?? 'idle').trim() || 'idle',
      teamName: selectedTeam?.name ?? '—',
      matchSource: matchSourceLabel,
      spaceCount: spaces.length,
      capsuleCount: totalCapsules,
      environments,
      spaces: spaceRows,
      deployments: this.devopsStore.webDeployments().length,
      builds: this.devopsStore.builds().length,
      incidents: this.devopsStore.incidents().length,
      notes: this.devopsStore.notes().slice(0, 3),
      lastUpdatedLabel: this.formatRelativeTime(this.devopsStore.lastLoadedAt()),
    };
  }

  get workspaceSummary() {
    const projects = this.store.projects() ?? [];
    const organizations = this.store.organizations() ?? [];
    const mappings = this.store.orgProjects() ?? [];
    const assignedProjects = new Set(mappings.map((mapping: any) => mapping.projectId).filter(Boolean));
    return {
      projects: projects.length,
      organizations: organizations.length,
      linkedProjects: assignedProjects.size,
      unassignedProjects: Math.max(0, projects.length - assignedProjects.size),
    };
  }

  get contactSummary() {
    return {
      supportTopics: this.contactTopics.length,
      upcomingFeatures: this.contactUpcomingFeatures.length,
      hasProjectContext: this.currentProjectName !== '—',
      hasOrganizationContext: this.currentOrganizationName !== '—',
    };
  }

  private memo<T>(id: string, key: string, make: () => T): T {
    const current = this._memo[id];
    if (current && current.key === key) return current.value as T;
    const value = make();
    this._memo[id] = { key, value };
    return value;
  }

  private cssVar(name: string, fallback: string): string {
    try {
      const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      return value || fallback;
    } catch {
      return fallback;
    }
  }

  private chartTheme() {
    const text = this.cssVar('--text-primary', '#0b1f35');
    const muted = this.cssVar('--text-muted', '#64748b');
    const grid = this.cssVar('--color-border', '#e5e7eb');
    const surface = this.cssVar('--color-surface', '#ffffff');
    const primary = this.cssVar('--primary-color', '#1f6feb');
    const key = `${text}|${muted}|${grid}|${surface}|${primary}`;
    return this.memo('chartTheme', key, () => ({ text, muted, grid, surface, primary }));
  }

  get defineChecklistChartData() {
    const readiness = this.defineSummary.readiness;
    const total = 4;
    const done =
      (String((this.workflow.defineDoc()?.data as any)?.name ?? '').trim() ? 1 : 0) +
      (String((this.workflow.defineDoc()?.data as any)?.description ?? '').trim() ? 1 : 0) +
      ((readiness.counts?.goals ?? 0) >= 1 ? 1 : 0) +
      ((readiness.counts?.features ?? 0) >= 5 ? 1 : 0);
    const theme = this.chartTheme();
    const bg = this.cssVar('--color-bg', '#f3f4f6');
    const key = `${done}|${total}|${theme.primary}|${bg}|${theme.grid}`;
    return this.memo('defineChecklistChartData', key, () => ({
      labels: ['Complete', 'Remaining'],
      datasets: [
        {
          label: 'Checklist',
          data: [done, Math.max(0, total - done)],
          backgroundColor: [theme.primary, bg],
          borderColor: theme.grid,
          borderWidth: 1,
        },
      ],
    }));
  }

  get defineTargetsChartData() {
    const readiness = this.defineSummary.readiness;
    const theme = this.chartTheme();
    const key = `${readiness.counts.goals}|${readiness.counts.features}|${readiness.counts.inScope}|${theme.primary}|${theme.muted}`;
    return this.memo('defineTargetsChartData', key, () => ({
      labels: ['Goals', 'Features', 'In scope'],
      datasets: [
        {
          label: 'Current',
          data: [readiness.counts.goals, readiness.counts.features, readiness.counts.inScope],
          backgroundColor: theme.primary,
        },
        {
          label: 'Target',
          data: [1, 5, 3],
          backgroundColor: theme.muted,
        },
      ],
    }));
  }

  get dbTopTablesChartData() {
    const summary = this.dbDesignSummary;
    const theme = this.chartTheme();
    const labels = summary.topTables.map((table) => table.name);
    const values = summary.topTables.map((table) => table.fields);
    const key = `${labels.join('|')}::${values.join(',')}::${theme.primary}`;
    return this.memo('dbTopTablesChartData', key, () => ({
      labels,
      datasets: [
        {
          label: 'Fields',
          data: values,
          backgroundColor: theme.primary,
        },
      ],
    }));
  }

  get dbFieldTypesChartData() {
    const fields = this.dbDesignSummary.fields;
    const theme = this.chartTheme();
    const key = `${fields.pk}|${fields.fk}|${fields.other}|${theme.primary}`;
    return this.memo('dbFieldTypesChartData', key, () => ({
      labels: ['PK', 'FK', 'Other'],
      datasets: [
        {
          label: 'Fields',
          data: [fields.pk, fields.fk, fields.other],
          backgroundColor: [theme.primary, '#7f56d9', '#94a3b8'],
        },
      ],
    }));
  }

  get ticketsByColumnChartData() {
    const summary = this.ticketsSummary;
    const theme = this.chartTheme();
    const labels = summary.byColumn.map((column) => column.title);
    const values = summary.byColumn.map((column) => column.count);
    const key = `${labels.join('|')}::${values.join(',')}::${theme.primary}`;
    return this.memo('ticketsByColumnChartData', key, () => ({
      labels,
      datasets: [
        {
          label: 'Cards',
          data: values,
          backgroundColor: theme.primary,
        },
      ],
    }));
  }

  get ticketsDoneChartData() {
    const summary = this.ticketsSummary;
    const key = `${summary.doneCards}|${summary.notDoneCards}`;
    return this.memo('ticketsDoneChartData', key, () => ({
      labels: ['Done', 'Not done'],
      datasets: [
        {
          label: 'Cards',
          data: [summary.doneCards, summary.notDoneCards],
          backgroundColor: ['#16a34a', '#94a3b8'],
        },
      ],
    }));
  }

  get defaultChartOptions() {
    const theme = this.chartTheme();
    const key = `${theme.muted}|${theme.grid}`;
    return this.memo('defaultChartOptions', key, () => ({
      animation: false,
      plugins: {
        legend: { labels: { color: theme.muted } },
      },
      scales: {
        x: { ticks: { color: theme.muted }, grid: { color: theme.grid } },
        y: { ticks: { color: theme.muted }, grid: { color: theme.grid }, beginAtZero: true },
      },
    })) as any;
  }

  get doughnutOptions() {
    const theme = this.chartTheme();
    const key = `${theme.muted}`;
    return this.memo('doughnutOptions', key, () => ({
      animation: false,
      plugins: { legend: { labels: { color: theme.muted } } },
    })) as any;
  }

  private isWorkflowKey(
    key: HomeDashboardSectionKey
  ): key is Extract<WorkflowSectionKey, HomeDashboardSectionKey> {
    return key === 'define' || key === 'db-design' || key === 'tickets' || key === 'users' || key === 'contact';
  }

  private userEmail(userId: string | null | undefined): string {
    if (!userId) return '';
    return this.userEmailById()[userId] ?? '';
  }

  private async hydrateUserEmails(userIds: string[]): Promise<void> {
    this.userEmailById.set(await lookupUserEmails(userIds, this.userEmailById()));
  }

  private toTimestamp(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    const numeric = Number(raw);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
    const parsed = Date.parse(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private formatRole(value: unknown): string {
    const raw = String(value ?? '').trim();
    if (!raw) return 'Member';
    return raw
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private toRecentMember(
    item: { userId?: string; role?: string; createdAt?: unknown; updatedAt?: unknown; timestamp?: unknown },
    scope: 'Project' | 'Organization'
  ): { key: string; label: string; meta: string; sortMs: number } {
    const timestamp =
      this.toTimestamp(item.updatedAt) ?? this.toTimestamp(item.createdAt) ?? this.toTimestamp(item.timestamp) ?? 0;
    const role = this.formatRole(item.role);
    const joined = timestamp > 0 ? this.formatRelativeTime(timestamp) : 'Recently';
    return {
      key: `${scope}:${String(item.userId ?? '').trim()}`,
      label: this.userEmail(item.userId) || String(item.userId ?? '').trim() || 'Unknown user',
      meta: `${scope} • ${role} • ${joined}`,
      sortMs: timestamp,
    };
  }

  private async loadMembershipData(projectId: string | null, organizationId: string | null): Promise<void> {
    const requestVersion = ++this.membershipRequestVersion;
    if (!projectId && !organizationId) return;

    try {
      const [projectUsersRes, orgUsersRes] = await Promise.all([
        projectId
          ? this.projectUsersService.find({ where: { projectId } as any })
          : Promise.resolve({ data: [] as any[] }),
        organizationId
          ? this.orgUsersService.find({ where: { organizationId } as any })
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const projectUsers = projectUsersRes.data ?? [];
      const orgUsers = orgUsersRes.data ?? [];
      if (requestVersion !== this.membershipRequestVersion) return;

      this.store.upsertEntities({ projectUsers, orgUsers });

      const userIds = Array.from(
        new Set([...projectUsers.map((item) => item.userId), ...orgUsers.map((item) => item.userId)].filter(Boolean))
      );
      await this.hydrateUserEmails(userIds);
    } catch {
      // Keep the dashboard resilient if membership lookups fail.
    }
  }

  private formatRelativeTime(ms: number | null): string {
    if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return '—';
    const delta = this.nowMs() - ms;
    if (!Number.isFinite(delta)) return '—';
    const sec = Math.floor(delta / 1000);
    if (sec < 10) return 'Just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 14) return `${day}d ago`;
    try {
      return new Date(ms).toLocaleDateString();
    } catch {
      return '—';
    }
  }

  openSectionDetail(titleOrKey: string) {
    const card = findHomeDashboardCard(titleOrKey);
    const key = card?.key ?? null;
    this.selectedSectionKey = key;
    this.selectedSectionTitle =
      card?.title ??
      (key ? (this.workflow.statuses().find((status) => status.key === key)?.title ?? titleOrKey) : titleOrKey);
    this.isExpanded = true;
  }

  async openHomeRoute(card: HomeDashboardCard): Promise<void> {
    await this.router.navigate([card.route]);
  }

  private async loadQuotationsSummary(projectId: string | null): Promise<void> {
    const requestVersion = ++this.quotationsRequestVersion;
    if (!projectId) {
      this.quotationsLoading = false;
      this.quotationsError = '';
      this.quotationsSummary = {
        total: 0,
        draft: 0,
        readyToSend: 0,
        sent: 0,
        history: 0,
        lastUpdatedLabel: '—',
        recentNames: [],
      };
      return;
    }

    this.quotationsLoading = true;
    this.quotationsError = '';
    try {
      const quotes = await this.quotationsService.listQuotations(projectId);
      if (requestVersion !== this.quotationsRequestVersion) return;

      const ordered = [...quotes].sort((left, right) => {
        const rightUpdatedAt = Date.parse(String(right.updatedAt ?? right.createdAt ?? '')) || 0;
        const leftUpdatedAt = Date.parse(String(left.updatedAt ?? left.createdAt ?? '')) || 0;
        return rightUpdatedAt - leftUpdatedAt;
      });
      const latestUpdatedAt = Date.parse(String(ordered[0]?.updatedAt ?? ordered[0]?.createdAt ?? '')) || 0;
      this.quotationsSummary = {
        total: ordered.length,
        draft: ordered.filter((quote) => quote.status === 'draft').length,
        readyToSend: ordered.filter((quote) => quote.status === 'ready_to_send').length,
        sent: ordered.filter((quote) => quote.status === 'sent').length,
        history: ordered.filter((quote) => ['accepted', 'rejected', 'expired'].includes(quote.status)).length,
        lastUpdatedLabel: this.formatRelativeTime(latestUpdatedAt),
        recentNames: ordered
          .map((quote) => String(quote.generatedName ?? '').trim())
          .filter(Boolean)
          .slice(0, 3),
      };
    } catch (error: any) {
      if (requestVersion !== this.quotationsRequestVersion) return;
      this.quotationsError = error?.message ?? 'Failed to load quotations';
      this.quotationsSummary = {
        total: 0,
        draft: 0,
        readyToSend: 0,
        sent: 0,
        history: 0,
        lastUpdatedLabel: '—',
        recentNames: [],
      };
    } finally {
      if (requestVersion === this.quotationsRequestVersion) {
        this.quotationsLoading = false;
      }
    }
  }
}